import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'

export type Meridiem = 'AM' | 'PM'
export type QuarterMinute = 0 | 15 | 30 | 45

/**
 * Keypad-style time picker that opens as a centered dialog.
 *
 * Uses MatDialog (not mat-menu) so the popup is always viewport-centered,
 * matching the old ngx-mat-timepicker behavior. This avoids positioning
 * issues when the trigger lives inside a mat-dialog or near viewport edges.
 */
@Component({
    standalone: false,
    selector: 'app-keypad-time-picker',
    templateUrl: './keypad-time-picker.component.html',
    styleUrls: ['./keypad-time-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KeypadTimePickerComponent {
    @Input() initialTime = ''
    @Input() defaultMeridiem: Meridiem | null = null

    @Output() timeSet = new EventEmitter<string>()
    @Output() cleared = new EventEmitter<void>()

    @ViewChild('keypadTmpl') keypadTmpl!: TemplateRef<any>

    readonly hours: ReadonlyArray<number> = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    readonly minutes: ReadonlyArray<QuarterMinute> = [0, 15, 30, 45]

    draftHour = 12
    draftMinute: QuarterMinute = 0
    draftMeridiem: Meridiem = 'AM'

    private dialogRef: MatDialogRef<any> | null = null

    constructor(private dialog: MatDialog) {}

    openPicker(event: MouseEvent): void {
        event.stopPropagation()
        if (this.dialogRef) return
        this.parseInitial(this.initialTime)
        this.dialogRef = this.dialog.open(this.keypadTmpl, {
            width: 'auto',
            hasBackdrop: true,
            panelClass: 'keypad-time-picker-dialog',
            autoFocus: false
        })
        this.dialogRef.afterClosed().subscribe(() => { this.dialogRef = null })
    }

    selectHour(h: number): void { this.draftHour = h }
    selectMinute(m: QuarterMinute): void { this.draftMinute = m }
    selectMeridiem(p: Meridiem): void { this.draftMeridiem = p }

    confirm(): void {
        this.timeSet.emit(this.formatDraft())
        this.dialogRef?.close()
    }

    clear(): void {
        this.cleared.emit()
        this.dialogRef?.close()
    }

    private formatDraft(): string {
        return `${this.draftHour}:${this.draftMinute.toString().padStart(2, '0')} ${this.draftMeridiem}`
    }

    private parseInitial(value: string): void {
        const fallback = this.smartDefault()
        if (!value) { this.applyDraft(fallback); return }

        const match = value.trim().toUpperCase().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/)
        if (!match) { this.applyDraft(fallback); return }

        const hour = parseInt(match[1], 10)
        const minute = parseInt(match[2], 10)
        const meridiem: Meridiem = (match[3] as Meridiem | undefined) ?? fallback.meridiem

        if (hour < 1 || hour > 12 || minute < 0 || minute > 59) { this.applyDraft(fallback); return }

        this.applyDraft({ hour, minute: this.snapMinute(minute), meridiem })
    }

    private snapMinute(m: number): QuarterMinute {
        const candidates: QuarterMinute[] = [0, 15, 30, 45]
        let best: QuarterMinute = 0
        let bestDist = Infinity
        for (const c of candidates) {
            const d = Math.abs(c - m)
            if (d < bestDist) { bestDist = d; best = c }
        }
        return best
    }

    private applyDraft(d: { hour: number; minute: QuarterMinute; meridiem: Meridiem }): void {
        this.draftHour = d.hour
        this.draftMinute = d.minute
        this.draftMeridiem = d.meridiem
    }

    private smartDefault(): { hour: number; minute: QuarterMinute; meridiem: Meridiem } {
        const now = new Date()
        const minute = this.snapMinute(now.getMinutes())
        const hours24 = now.getHours()
        const meridiem: Meridiem = this.defaultMeridiem ?? (hours24 >= 12 ? 'PM' : 'AM')
        const hour12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24
        return { hour: hour12, minute, meridiem }
    }
}
